import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { SimilarDatasetsComponent } from '../../../shared/components/similar-datasets/similar-datasets.component';
import { AssetCardData } from '../../../shared/components/asset-card/asset-card.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { DiscussionItem } from '../../discussions/discussion-home/discussion-home.component';
import { CatalogueApiService, UserInteractionApiService, SearchCriteria, DiscussionApiService } from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { UsecasePreviewService, UsecasePreviewData } from '../../../shared/services/usecase-preview.service';

@Component({
  selector: 'app-usecase-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    SimilarDatasetsComponent,
    ModalComponent,
    LoginModalComponent
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class UsecaseDetailComponent implements OnInit, OnDestroy, OnChanges {
  private catalogueApiService = inject(CatalogueApiService);
  private helperService = inject(HelperService);
  private formatService = inject(FormatService);
  private route = inject(ActivatedRoute);
  private usecasePreviewService = inject(UsecasePreviewService);
  private keycloak = inject(KeycloakService);
  private userInteractionApiService = inject(UserInteractionApiService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakSubscription?: Subscription;

  usecaseDiscussions: DiscussionItem[] = [];
  usecaseDiscussionsLoading = false;
  usecaseDiscussionsError: string | null = null;

  // Input to accept preview data when embedded (for inline preview in Step 2)
  @Input() previewDataInput: UsecasePreviewData | null = null;
  @Input() embeddedMode: boolean = false; // When true, component is embedded (no header/footer)

  usecaseId: string | null = null;
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Usecases', route: '/usecases' },
      { label: 'Usecase Details' }
    ],
    title: 'Usecase Details',
    description: 'Loading usecase information...'
  };
  similarUsecases: AssetCardData[] = [];
  usecaseDescription: string = '';
  usecaseTags: string[] = [];
  showTagsModal: boolean = false;
  readonly MAX_VISIBLE_TAGS = 8;

  // Sections from API
  sections: Array<{
    title?: string;
    content?: string;
    subsections?: Array<{
      subtitle?: string;
      content?: string;
      points?: string[];
    }>;
  }> = [];

  usecaseDetails: {
    organisation?: string;
    sector?: string;
    organizationType?: string;
    resources?: Array<{ name: string; url?: string }>;
    viewCount?: number;
    lastUpdated?: string;
    [key: string]: any;
  } = {};

  usecaseCardDetails: {
    badge?: string;
    createdAt?: string;
    viewCount?: number;
    lastUpdated?: string;
  } = {};

  // Activity Overview metrics (from Item API) - Like and View only, like Model
  activityMetrics = {
    likes: 0,
    downloads: 0,
    views: 0,
    bookmarks: 0
  };

  isLoading: boolean = true;
  isLoggedIn: boolean = false;
  isBookmarked: boolean = false;
  isLiked: boolean = false;
  showLoginModal: boolean = false;

  activeTab: 'usecase' | 'discussion' = 'usecase';

  get visibleTags(): string[] {
    return this.usecaseTags.slice(0, this.MAX_VISIBLE_TAGS);
  }

  get remainingTagsCount(): number {
    return Math.max(0, this.usecaseTags.length - this.MAX_VISIBLE_TAGS);
  }

  get allTags(): string[] {
    return this.usecaseTags;
  }

  get hasUsecaseDetails(): boolean {
    return this.usecaseDetails && Object.keys(this.usecaseDetails).length > 0;
  }

  setActiveTab(tab: 'usecase' | 'discussion'): void {
    this.activeTab = tab;
    if (tab === 'discussion') {
      this.loadUsecaseDiscussions();
    }
  }

  async ngOnInit(): Promise<void> {
    await this.checkLoginStatus();
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          this.isLoggedIn = true;
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          await this.checkLoginStatus();
          if (keycloakEvent.type === KeycloakEventType.OnAuthLogout) {
            this.isBookmarked = false;
            this.isLiked = false;
          }
        }
      }
    );

    // If embedded mode and preview data is provided via Input, use it directly
    if (this.embeddedMode && this.previewDataInput) {
      console.log('Loading embedded preview data from Input:', this.previewDataInput);
      this.loadPreviewData(this.previewDataInput);
      return;
    }

    // Otherwise, subscribe to route params (standalone page mode)
    this.route.paramMap.subscribe(params => {
      this.usecaseId = params.get('id');
      console.log('Usecase Detail Component initialized with ID:', this.usecaseId);
      
      // If ID is 'preview', check for preview data (from usecase creation/editing)
      if (this.usecaseId === 'preview') {
        const previewDataResult = this.usecasePreviewService.getPreviewData();
        if (previewDataResult !== null && previewDataResult !== undefined) {
          const previewData = previewDataResult as UsecasePreviewData;
          if (previewData.isPreview === true) {
            // Load preview data instead of API data
            console.log('Loading preview data:', previewData);
            this.loadPreviewData(previewData);
            // Don't load similar usecases for preview
            return;
          }
        }
        // If preview ID but no preview data, show error
        this.headerConfig.description = 'Preview data not found. Please try again.';
        this.isLoading = false;
        return;
      }
      
      if (!this.usecaseId) {
        console.error('No usecase ID found in route parameters');
        this.headerConfig.description = 'Invalid usecase ID. Please try again.';
        this.isLoading = false;
        return;
      }
      
      // Otherwise, load from API as usual
      this.loadUsecaseDetails();
    });
  }

  async loadUsecaseDiscussions(): Promise<void> {
    if (!this.usecaseId) {
      this.usecaseDiscussions = [];
      return;
    }
    this.usecaseDiscussionsLoading = true;
    this.usecaseDiscussionsError = null;
    this.cdr.detectChanges();
    try {
      const response = await this.discussionApiService.getDiscussionChoices({
        filters: {
          type: [],
          status: ['APPROVED'],
          tags: [],
          sub_category_id: this.usecaseId
        },
        pinned: true,
        sort_by: 'newest',
        page: 1,
        limit: 10
      });
      const discussions = response?.data?.discussions ?? response?.discussions ?? [];
      this.usecaseDiscussions = (Array.isArray(discussions) ? discussions : []).map((item: any) =>
        this.mapDiscussionChoiceToItem(item)
      );
    } catch (error) {
      console.error('Error loading usecase discussions:', error);
      this.usecaseDiscussionsError = 'Failed to load discussions. Please try again.';
      this.usecaseDiscussions = [];
    } finally {
      this.usecaseDiscussionsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapDiscussionChoiceToItem(item: any): DiscussionItem {
    const author = item?.user?.name ?? 'Unknown';
    const subCategory = item?.sub_category ?? this.headerConfig.title ?? '';
    const publishedTime = item?.published_time ?? item?.created_at ?? '';
    return {
      id: item?.id ?? '',
      title: item?.title ?? '',
      description: `${author} · ${subCategory}`,
      author,
      category: item?.category ?? 'DATA_BANKS',
      replies: item?.votes ?? 0,
      views: item?.view_count ?? 0,
      lastActivity: publishedTime ? `Published ${this.formatRelativeTime(publishedTime)}` : '',
      isPinned: item?.is_pinned ?? false,
      isBookmarked: item?.is_bookmarked ?? false,
      isVoted: item?.is_voted ?? false,
      status: item?.status,
      subCategoryId: item?.sub_category_id,
      assetType: 'Usecase',
      assetName: subCategory
    };
  }

  private formatRelativeTime(dateString: string): string {
    const dateObj = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return dateObj.toLocaleDateString();
  }

  navigateToDiscussion(discussionId: string): void {
    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId]);
    }).catch(() => {
      this.router.navigate(['/discussions', discussionId]);
    });
  }

  createNewDiscussion(): void {
    this.router.navigate(['/discussions/create-new'], {
      queryParams: { category: 'GETTING_STARTED', tab: 'getting-started' }
    });
  }

  ngOnDestroy(): void {
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async onBookmarkClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.usecaseId) return;
    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.usecaseId,
        assetType: 'USECASE',
        actionType,
      });
      this.isBookmarked = !this.isBookmarked;
      if (this.isBookmarked) {
        this.toasterService.success("You've bookmarked the Usecase successfully.");
      } else {
        this.toasterService.success('Bookmark removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  async onLikeClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.usecaseId) return;
    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.usecaseId,
        assetType: 'USECASE',
        actionType,
      });
      this.isLiked = !this.isLiked;
      // Optimistically update like count immediately (item API may have eventual consistency)
      this.activityMetrics.likes = Math.max(0, (this.activityMetrics.likes ?? 0) + (this.isLiked ? 1 : -1));
      if (this.isLiked) {
        this.toasterService.success("You've liked the Usecase successfully.");
      } else {
        this.toasterService.success('Like removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  /** Refresh activity metrics from item API after like */
  private async refreshActivityMetrics(): Promise<void> {
    if (!this.usecaseId) return;
    try {
      const response = await this.catalogueApiService.getItemById(this.usecaseId);
      let data = response;
      if (response?.result) data = Array.isArray(response.result) ? response.result[0] : response.result;
      else if (response?.data) data = Array.isArray(response.data) ? response.data[0] : response.data;
      else if (response?.items?.[0]) data = response.items[0];
      else if (Array.isArray(response) && response.length > 0) data = response[0];
      if (!data) return;
      const metrics = data.metrics || data;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
    } catch {
      // Ignore - keep existing metrics
    }
  }

  async checkBookmarkAndLikeStatus(): Promise<void> {
    if (!this.isLoggedIn || !this.usecaseId || this.embeddedMode) return;
    try {
      const response = await this.userInteractionApiService.userInteractions({
        assetId: this.usecaseId,
        assetType: 'USECASE',
      });
      const items: any[] =
        (response?.result && Array.isArray(response.result) ? response.result : null) ||
        (response?.results && Array.isArray(response.results) ? response.results : null) ||
        (response?.data && Array.isArray(response.data) ? response.data : null) ||
        [];
      const first = items[0] ?? {};
      const userInteraction = first?.userInteraction;
      const ad = first?.assetDetails ?? first;

      const bookmarked =
        typeof userInteraction?.bookmarked === 'boolean' ? userInteraction.bookmarked :
        typeof first?.isBookmarked === 'boolean' ? first.isBookmarked :
        typeof first?.bookmarked === 'boolean' ? first.bookmarked :
        typeof ad?.bookmarked === 'boolean' ? ad.bookmarked : null;
      if (bookmarked !== null) {
        this.isBookmarked = bookmarked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isBookmarked = actionTypes.includes('BOOKMARK');
      }

      const liked =
        typeof userInteraction?.liked === 'boolean' ? userInteraction.liked :
        typeof first?.isLiked === 'boolean' ? first.isLiked :
        typeof first?.liked === 'boolean' ? first.liked :
        typeof ad?.liked === 'boolean' ? ad.liked : null;
      if (liked !== null) {
        this.isLiked = liked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isLiked = actionTypes.includes('LIKE');
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking bookmark/like status:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When previewDataInput changes (in embedded mode), reload the data
    if (this.embeddedMode && changes['previewDataInput'] && this.previewDataInput) {
      console.log('Preview data Input changed, reloading:', this.previewDataInput);
      this.loadPreviewData(this.previewDataInput);
    }
  }

  async loadUsecaseDetails(): Promise<void> {
    if (!this.usecaseId) {
      console.error('Cannot load usecase details: usecaseId is null or undefined');
      this.headerConfig.description = 'Invalid usecase ID. Please try again.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      console.log('Loading usecase details for ID:', this.usecaseId);
      const response = await this.catalogueApiService.getItemById(this.usecaseId);
      console.log('API Response received:', response);

      let usecaseData = response;
      
      // Handle different response structures
      if (response?.result) {
        if (Array.isArray(response.result)) {
          usecaseData = response.result.length > 0 ? response.result[0] : null;
        } else {
          usecaseData = response.result;
        }
      } else if (Array.isArray(response) && response.length > 0) {
        usecaseData = response[0];
      } else if (response && typeof response === 'object' && !response.result) {
        // Response might be the usecase data directly
        usecaseData = response;
      }

      if (!usecaseData) {
        console.error('No usecase data found in response. Response structure:', JSON.stringify(response, null, 2));
        this.headerConfig.description = 'Failed to load usecase information. No data found in response.';
        this.isLoading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        return;
      }

      console.log('Usecase data loaded:', usecaseData);
      console.log('Sections in usecaseData:', usecaseData.sections);

      // Extract short description
      let shortDescription = usecaseData.shortDescription || usecaseData.summary || '';
      if (!shortDescription && usecaseData.description) {
        shortDescription = usecaseData.description.length > 150
          ? usecaseData.description.substring(0, 150).trim() + '...'
          : usecaseData.description;
      }

      // Update header config
      const usecaseTitle = usecaseData.label || usecaseData.name || 'Usecase Details';
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Usecases', route: '/usecases' },
          { label: usecaseTitle }
        ],
        title: usecaseTitle,
        description: shortDescription || 'Loading usecase information...',
        assetId: this.usecaseId || usecaseData.id || ''
      };

      if (!this.embeddedMode && this.isLoggedIn) {
        await this.checkBookmarkAndLikeStatus();
      }

      // Extract description
      this.usecaseDescription = usecaseData.description || '';

      // Extract sections (Introduction, Challenges, Solutions, etc.)
      this.sections = [];
      
      // First, try to get sections as an array
      if (usecaseData.sections && Array.isArray(usecaseData.sections) && usecaseData.sections.length > 0) {
        this.sections = usecaseData.sections;
        console.log('Using sections array from API:', this.sections);
      } else {
        // If sections array doesn't exist, try to build sections from individual fields
        const sectionFields = [
          { key: 'introduction', title: 'Introduction' },
          { key: 'challenges', title: 'Challenges' },
          { key: 'solutions', title: 'Solutions' },
          { key: 'methodology', title: 'Methodology' },
          { key: 'objectives', title: 'Objectives' },
          { key: 'overview', title: 'Overview' },
          { key: 'background', title: 'Background' }
        ];

        sectionFields.forEach(field => {
          if (usecaseData[field.key]) {
            const content = typeof usecaseData[field.key] === 'string' 
              ? usecaseData[field.key] 
              : JSON.stringify(usecaseData[field.key]);
            
            this.sections.push({
              title: field.title,
              content: content
            });
          }
        });

        // If still no sections, use description as introduction
        if (this.sections.length === 0 && this.usecaseDescription) {
          this.sections.push({
            title: 'Introduction',
            content: this.usecaseDescription
          });
        }
      }
      
      console.log('Extracted sections:', this.sections);

      // Extract tags
      this.usecaseTags = [];
      if (usecaseData.tags && Array.isArray(usecaseData.tags)) {
        this.usecaseTags = usecaseData.tags;
      }

      // Extract usecase details
      const organisation = usecaseData.provider || usecaseData.organisation || usecaseData.organization || '';
      const sector = usecaseData.sector || usecaseData.industry || '';
      const organizationType = usecaseData.organizationType || usecaseData.organisationType || usecaseData.orgType || '';
      
      // Extract resources (could be links, APIs, etc.)
      let resources: Array<{ name: string; url?: string }> = [];
      if (usecaseData.resources && Array.isArray(usecaseData.resources)) {
        resources = usecaseData.resources.map((r: any) => ({
          name: typeof r === 'string' ? r : (r.name || r.label || r),
          url: typeof r === 'object' ? (r.url || r.link || r.href) : undefined
        }));
      } else if (usecaseData.resourceLinks && Array.isArray(usecaseData.resourceLinks)) {
        resources = usecaseData.resourceLinks.map((r: any) => ({
          name: typeof r === 'string' ? r : (r.name || r.label || r),
          url: typeof r === 'object' ? (r.url || r.link || r.href) : undefined
        }));
      } else if (usecaseData.apiLinks && Array.isArray(usecaseData.apiLinks)) {
        resources = usecaseData.apiLinks.map((r: any) => ({
          name: typeof r === 'string' ? r : (r.name || r.label || r),
          url: typeof r === 'object' ? (r.url || r.link || r.href) : undefined
        }));
      }

      this.usecaseDetails = {
        organisation: organisation,
        sector: sector,
        organizationType: organizationType,
        resources: resources,
        viewCount: usecaseData.viewCount || usecaseData.views || 0,
        lastUpdated: this.formatService.formatDate(usecaseData.itemCreatedAt || usecaseData.createdAt || usecaseData.lastUpdated) || ''
      };

      const badge = this.helperService.extractAccessBadge(usecaseData);
      this.usecaseCardDetails = {
        badge: badge || 'Public',
        createdAt: this.formatService.formatDateInIST(usecaseData.itemCreatedAt || usecaseData.createdAt) || '',
        viewCount: usecaseData.viewCount || usecaseData.views || 0,
        lastUpdated: this.formatService.formatDate(usecaseData.itemCreatedAt || usecaseData.createdAt || usecaseData.lastUpdated) || ''
      };

      // Populate Activity Overview from Item API metrics
      const metrics = usecaseData.metrics || usecaseData;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;

      this.isLoading = false;
      
      // Trigger change detection to ensure view updates
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Load similar usecases based on tags (after usecaseTags is set)
      this.loadSimilarUsecases().catch(err => console.error('Error loading similar usecases:', err));
      
      // Also trigger change detection in next microtask for robustness
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    } catch (error) {
      console.error('Error loading usecase details:', error);
      // Update header to show error state
      this.headerConfig = {
        ...this.headerConfig,
        description: 'Failed to load usecase information. Please try again later.'
      };
      // Clear sections and details on error
      this.sections = [];
      this.usecaseDetails = {};
      this.usecaseTags = [];
      this.isLoading = false;
      
      // Trigger change detection on error
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  /**
   * Load preview data from usecase creation/editing
   * This method is called when user navigates from usecase creation to preview
   */
  loadPreviewData(previewData: UsecasePreviewData): void {
    this.isLoading = true;
    
    try {
      console.log('Loading preview data:', previewData);
      
      // Set sections from parsed content
      this.sections = previewData.sections || [];
      
      // Update header config for preview - use metadata from Step 1
      const previewTitle = previewData.title || 'Usecase Preview';
      const shortDescription = previewData.shortDescription || previewData.description || 'Preview of usecase content';
      
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Usecases', route: '/usecases' },
          { label: previewTitle }
        ],
        title: previewTitle,
        description: shortDescription.length > 150 
          ? shortDescription.substring(0, 150).trim() + '...' 
          : shortDescription,
        assetId: 'preview'
      };

      // Extract description from Step 1 or first section
      this.usecaseDescription = previewData.description || '';
      if (!this.usecaseDescription && this.sections.length > 0 && this.sections[0].content) {
        this.usecaseDescription = this.sections[0].content;
      } else if (!this.usecaseDescription && this.sections.length > 0) {
        // If first section has subsections, create a description
        this.usecaseDescription = `This usecase contains ${this.sections.length} section(s) with detailed information.`;
      }

      // Extract tags from industries/organisation types
      this.usecaseTags = [];
      if (previewData.industries && Array.isArray(previewData.industries)) {
        this.usecaseTags.push(...previewData.industries);
      }
      if (previewData.organisationTypes && Array.isArray(previewData.organisationTypes)) {
        this.usecaseTags.push(...previewData.organisationTypes);
      }
      if (previewData.tags && Array.isArray(previewData.tags)) {
        this.usecaseTags.push(...previewData.tags);
      }

      // Set usecase details from metadata
      const organisationTypes = previewData.organisationTypes || [];
      const organisation = organisationTypes.length > 0 ? organisationTypes.join(', ') : 'Preview';
      
      const industries = previewData.industries || [];
      const sector = industries.length > 0 ? industries.join(', ') : '';
      
      // Extract organizationType from organisationTypes array (take first one or join if multiple)
      const organizationType = organisationTypes.length > 0 
        ? (organisationTypes.length === 1 ? organisationTypes[0] : organisationTypes.join(', '))
        : '';

      this.usecaseDetails = {
        organisation: previewData.organisation || organisation,
        sector: previewData.sector || sector,
        organizationType: organizationType,
        resources: previewData.resources || [],
        viewCount: 0,
        lastUpdated: previewData.createdAt ? this.formatService.formatDate(previewData.createdAt) : ''
      };

      this.usecaseCardDetails = {
        badge: 'Preview',
        createdAt: previewData.createdAt ? this.formatService.formatDateInIST(previewData.createdAt) : '',
        viewCount: 0,
        lastUpdated: previewData.createdAt ? this.formatService.formatDate(previewData.createdAt) : ''
      };

      this.isLoading = false;
      console.log('Preview data loaded successfully. Sections:', this.sections);
    } catch (error) {
      console.error('Error loading preview data:', error);
      this.headerConfig.description = 'Failed to load preview data. Please try again.';
      this.sections = [];
      this.usecaseDetails = {};
      this.usecaseTags = [];
      this.isLoading = false;
    }
  }

  async loadSimilarUsecases(): Promise<void> {
    if (!this.usecaseTags || this.usecaseTags.length === 0) {
      this.similarUsecases = [];
      return;
    }
    try {
      const searchCriteria: SearchCriteria[] = [
        { field: 'tags', searchType: 'term', values: this.usecaseTags },
        { field: 'type', searchType: 'term', values: ['adex:Apps'] }
      ];
      const response = await this.catalogueApiService.searchAllAssets(
        '', [], [], 1, 10, 'name:asc;itemCreatedAt:asc;', searchCriteria
      );
      let items: any[] = [];
      if (Array.isArray(response)) items = response;
      else if (response?.result && Array.isArray(response.result)) items = response.result;
      else if (response?.items && Array.isArray(response.items)) items = response.items;
      else if (response?.data && Array.isArray(response.data)) items = response.data;
      else if (response?.data?.result && Array.isArray(response.data.result)) items = response.data.result;
      else if (response?.data?.items && Array.isArray(response.data.items)) items = response.data.items;
      this.similarUsecases = items
        .filter((item: any) => (item.id || item['@id'] || '') !== this.usecaseId)
        .map((item: any) => this.mapApiItemToAssetCard(item));
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading similar usecases:', error);
      this.similarUsecases = [];
    }
  }

  private mapApiItemToAssetCard(item: any): AssetCardData {
    const id = item.id || item['@id'] || '';
    const title = item.label || item.name || 'Untitled';
    const provider = item.provider || item.organisation || item.organization || item.provider_name || '';
    const tags: string[] = Array.isArray(item.tags) ? [...item.tags] : [];
    const metadata: any = {};
    if (item.resourceServer) {
      const rs = Array.isArray(item.resourceServer) ? item.resourceServer[0] : item.resourceServer;
      metadata.type = rs?.type || rs?.name || '';
    }
    metadata.format = item.fileFormat || item.format || '';
    metadata.date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || item.created_at || new Date());
    const badgeLabel = this.helperService.extractAccessBadge(item) || 'Public';
    const badgeColor = this.helperService.getBadgeColorVariant(badgeLabel);
    return { id, title, provider, tags, metadata, badge: { label: badgeLabel, color: badgeColor as any } };
  }

  openTagsModal(): void {
    this.showTagsModal = true;
  }

  onResourceClick(resource: { name: string; url?: string }): void {
    if (resource.url) {
      window.open(resource.url, '_blank');
    }
  }

  /** Date part from formatted string (e.g. "02-Feb-2026") - match Dataset details */
  getCreatedAtDate(createdAt: string): string {
    if (!createdAt) return '';
    const parts = createdAt.split(' ');
    return parts[0] || '';
  }

  /** Time part from formatted string (e.g. "11:43 IST") - match Dataset details */
  getCreatedAtTime(createdAt: string): string | null {
    if (!createdAt) return null;
    const parts = createdAt.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : null;
  }
}
